import React from 'react'
import { usePermission } from '@/hooks/use-permission'

interface PermissionGuardProps {
    /**
     * Single permission or array of permissions required to show the content.
     */
    permission: string | string[]
    /**
     * Strategy for multiple permissions. 
     * 'any' - show if user has at least one of the permissions.
     * 'all' - show only if user has all of the permissions.
     * Defaults to 'any'.
     */
    require?: 'any' | 'all'
    /**
     * Optional fallback to render if the user doesn't have the required permission(s).
     */
    fallback?: React.ReactNode
    /**
     * The content to show if permissions are met.
     */
    children: React.ReactNode
}

/**
 * Declarative component for conditional rendering based on user permissions.
 */
export function PermissionGuard({
    permission,
    require = 'any',
    fallback = null,
    children
}: PermissionGuardProps) {
    const { can, hasAny, hasAll } = usePermission()

    let hasAccess = false

    if (Array.isArray(permission)) {
        hasAccess = require === 'all' ? hasAll(permission) : hasAny(permission)
    } else {
        hasAccess = can(permission)
    }

    if (!hasAccess) {
        return <>{fallback}</>
    }

    return <>{children}</>
}
